import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CATEGORICAL_COLORS } from '@/src/config';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { toLabelMap } from '@/src/segmentation/io/import';
import { readImage } from '@/src/io/readWriteImage';
import { useToolStore } from '@/src/store/tools';
import { usePaintToolStore } from '@/src/store/tools/paint';
import { Tools } from '@/src/store/tools/types';
import { PaintMode } from '@/src/core/tools/paint';
import { assetFile } from './api';
import type { DentalAnnotations, DentalAsset, DentalInstance, DentalLandmark, DentalLabelSchema } from './types';
import { assertSameGrid, exportVolumeLabels } from './volumeAnnotations';
import { createBrushHistory } from './brushHistory';
import { normalizeAssetGrid } from './assetGrid';
import { planSliceFill, applySliceFill } from './sliceFill';
import { useDentalLoadingStore } from './loadingStore';

type EditingInstance = DentalInstance & { segmentId: string };
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const useDentalCbctStore = defineStore('dental-cbct', () => {
  const imageId = ref(''); const frameId = ref(''); const instances = ref<EditingInstance[]>([]);
  const landmarks = ref<DentalLandmark[]>([]); const dirty = ref(false); const labelsDirty = ref(false);
  const labelAssetId = ref<string | null>(null); const mode = ref<'navigate' | 'window' | 'paint' | 'erase' | 'select' | 'landmark' | 'fill' | 'crosshairs'>('navigate');
  const fillError = ref(''); const filledVoxelCount = ref(0);
  const labelSchema = ref<DentalLabelSchema | null>(null);
  const restrictedLabels = computed(() => labelSchema.value?.id === 'permanent34');
  const landmarkName = ref('Landmark 1');
  const segmentation = useSegmentationStore(); const registry = useSegmentStore().segments;
  function changed() { dirty.value = true; labelsDirty.value = true; }
  const history = createBrushHistory(() => imageId.value, changed);
  const selected = computed(() => instances.value.find(row => row.segmentId === registry.selectedSegmentId.value));
  function select(id: string) { registry.selectSegment(id); }
  function colorAt(index: number): DentalInstance['color'] { return [...CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length], 255] as DentalInstance['color']; }
  function mint(row: DentalInstance) { return registry.mintSegment({ name: registry.uniqueName(row.name), color: row.color, visible: row.visible, locked: row.locked }); }
  async function loadAnnotations(annotations: DentalAnnotations, asset?: DentalAsset, schema: DentalLabelSchema | null = labelSchema.value) {
    setMode('navigate');
    labelSchema.value = schema;
    if (schema && (asset?.labelValues || []).some(value => !schema.labels.some(row => row.labelValue === value))) {
      throw new Error('此版本含固定34标签之外的值；请使用已对齐的34标签版本。');
    }
    instances.value.forEach(row => registry.deleteSegment(row.segmentId));
    const byValue = new Map(Object.entries(asset?.labelDefinitions || {}).map(([name, value]) => [value, name]));
    const embedded = new Map(asset?.segmentDefinitions?.map(row => [row.labelValue, row]));
    let rows = annotations.instances.length ? clone(annotations.instances) : (asset?.labelValues || []).map((value, index): DentalInstance => ({
      id: crypto.randomUUID(), labelValue: value, name: byValue.get(value) || `Label ${value}`, fdi: null,
      color: embedded.get(value)?.color || colorAt(index), visible: true, locked: false,
    }));
    if (schema) rows = schema.labels.map((definition, index) => {
      const previous = rows.find(row => row.labelValue === definition.labelValue);
      return { ...definition, id: previous?.id || crypto.randomUUID(),
        color: previous?.color || colorAt(index), visible: previous?.visible ?? true, locked: previous?.locked ?? false,
        opacity: definition.fdi === null ? previous?.opacity ?? definition.opacity : 1 };
    });
    instances.value = [];
    if (asset) {
      const loading = useDentalLoadingStore(); loading.step('下载标注');
      const file = await assetFile(asset, loading.transfer); loading.step('解析标注');
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      const decoded = (await readImage(file)).image;
      normalizeAssetGrid(decoded, asset);
      const parent = useImageCacheStore().getVtkImageData(imageId.value)!;
      assertSameGrid(parent, decoded);
      const { labelmap, excluded } = toLabelMap(decoded);
      if (excluded) throw new Error('Label data contains unsupported values; import was refused.');
      loading.step('准备标注图层', 0);
      const masks = await segmentation.splitLabelmapIntoMasksAsync(imageId.value, labelmap, rows.map(row => ({
        value: row.labelValue, name: row.name, color: row.color, visible: row.visible, locked: row.locked,
      })), value => { loading.percent = Math.floor(value * 100); });
      instances.value = rows.map((row, i) => ({ ...row, segmentId: masks[i].segmentId }));
      // The decoded interchange image is temporary, not another source volume.
      if (labelmap !== decoded) labelmap.delete(); decoded.delete();
    } else instances.value = rows.map(row => ({ ...row, segmentId: mint(row) }));
    landmarks.value = clone(annotations.landmarks); labelAssetId.value = annotations.labelmapAssetId;
    history.clear(); usePaintToolStore().setBrushLayers(1); dirty.value = false; labelsDirty.value = false;
    if (instances.value.length) select(instances.value[0].segmentId);
  }
  function addInstance(value: number, name: string, fdi: number | null = null) {
    if (restrictedLabels.value) throw new Error('此项目固定为32恒牙和上下颌骨，不能新增其他标签。');
    if (!Number.isInteger(value) || value < 1 || value > 65535 || instances.value.some(row => row.labelValue === value)) throw new Error('Choose an unused integer label value between 1 and 65535.');
    if (!name.trim()) throw new Error('An instance name is required.');
    const row: DentalInstance = { id: crypto.randomUUID(), labelValue: value, name: registry.uniqueName(name.trim()), fdi,
      color: colorAt(instances.value.length), visible: true, locked: false };
    const segmentId = mint(row); instances.value.push({ ...row, segmentId }); select(segmentId); dirty.value = true;
  }
  function updateInstance(row: EditingInstance, patch: Partial<DentalInstance>) {
    if (restrictedLabels.value) {
      const expected = labelSchema.value!.labels.find(label => label.labelValue === row.labelValue)!;
      if ((patch.name !== undefined && patch.name !== expected.name) || (patch.fdi !== undefined && patch.fdi !== expected.fdi) ||
          (patch.labelValue !== undefined && patch.labelValue !== expected.labelValue)) throw new Error('固定标签名称、编号和FDI不可改动。');
      if (patch.opacity !== undefined && (expected.fdi !== null ? patch.opacity !== 1 : !(patch.opacity > 0 && patch.opacity < 1))) throw new Error('牙齿保持不透明，颌骨保持半透明。');
    }
    Object.assign(row, patch); registry.updateSegment(row.segmentId, { name: row.name, color: row.color, visible: row.visible, locked: row.locked }); dirty.value = true;
  }
  function setMode(value: typeof mode.value) {
    mode.value = value;
    fillError.value = '';
    const tools = useToolStore(); const paint = usePaintToolStore();
    if (['paint', 'erase', 'select'].includes(value)) {
      paint.setMode(value === 'erase' ? PaintMode.Erase : value === 'select' ? PaintMode.Eyedropper : PaintMode.CirclePaint);
      tools.setCurrentTool(Tools.Paint);
    } else tools.setCurrentTool(value === 'window' ? Tools.WindowLevel : value === 'crosshairs' ? Tools.Crosshairs : Tools.Pan);
  }
  function fillSlice(axis: number, ijk: [number,number,number]) {
    fillError.value = ''; filledVoxelCount.value = 0;
    try {
      if (!selected.value) throw new Error('Select a target label before filling.');
      const fill = planSliceFill(imageId.value,axis,ijk,instances.value,selected.value.segmentId);
      if (!fill) return;
      applySliceFill(imageId.value,fill); history.recordFill(fill); filledVoxelCount.value = fill.indices.length;
    } catch (error) { fillError.value = (error as Error).message; }
  }
  function addLandmark(position: [number, number, number]) {
    landmarks.value.push({ id: crypto.randomUUID(), name: landmarkName.value || 'Landmark', frameId: frameId.value, position: [...position] });
    landmarkName.value = `Landmark ${landmarks.value.length + 1}`; dirty.value = true;
  }
  function removeLandmark(id: string) { landmarks.value = landmarks.value.filter(p => p.id !== id); dirty.value = true; }
  function renameLandmark(id: string, name: string) { const point = landmarks.value.find(row => row.id === id); if (point) { point.name = name; dirty.value = true; } }
  function annotations(): DentalAnnotations {
    return { labelmapAssetId: labelAssetId.value, meshLabelsAssetId: null, instances: clone(instances.value.map(({ segmentId, ...row }) => row)), landmarks: clone(landmarks.value), registration: null };
  }
  function exportLabels(format = 'nii.gz') { return exportVolumeLabels(imageId.value, new Map(instances.value.map(row => [row.segmentId, row.labelValue])), format, instances.value); }
  function markSaved(assetId: string | null) { labelAssetId.value = assetId; dirty.value = false; labelsDirty.value = false; }
  return { imageId, frameId, instances, landmarks, selected, mode, landmarkName, dirty, labelsDirty, labelAssetId, labelSchema, restrictedLabels,
    undoCount: history.undoCount, redoCount: history.redoCount, historyNotice: history.notice,
    undo: history.undoStroke, redo: history.redoStroke, dispose: history.dispose,
    loadAnnotations, addInstance, updateInstance, select, setMode, fillSlice, fillError, filledVoxelCount,
    addLandmark, removeLandmark, renameLandmark, annotations, exportLabels, markSaved };
});
